import React, { useState } from 'react';
import type { VideoReference, VideoReferenceRole } from './videoStudioTypes';

interface VideoReferencesEditorProps {
  references: VideoReference[];
  onChange: (updated: VideoReference[]) => void;
}

export const VideoReferencesEditor: React.FC<VideoReferencesEditorProps> = ({
  references,
  onChange,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newRole, setNewRole] = useState<VideoReferenceRole>('product');
  const [newPreservation, setNewPreservation] = useState('');

  const handleAdd = () => {
    if (!newUrl.trim()) return;
    const ref: VideoReference = {
      role: newRole,
      asset_url: newUrl.trim(),
      label: newLabel.trim() || 'Nova Referência',
      preservation_notes: newPreservation.trim() || 'Preservar traços e identidade visual',
      is_active: 1,
    };
    onChange([...references, ref]);
    setNewUrl('');
    setNewLabel('');
    setNewPreservation('');
  };

  const handleRemove = (index: number) => {
    const updated = references.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleUpdate = (index: number, patch: Partial<VideoReference>) => {
    const updated = [...references];
    updated[index] = { ...updated[index], ...patch };
    onChange(updated);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Referências Visuais & Consistência</h4>
          <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)' }}>
            Defina o avatar oficial, o produto ou a arte base que o Flow deve preservar sem distorções.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {references.map((ref, idx) => (
          <div key={ref.id || idx} className="vstudio-card" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <img
                src={ref.asset_url}
                alt={ref.label}
                style={{ width: '60px', height: '60px', borderRadius: '0.5rem', objectFit: 'cover', background: '#000' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/100x100?text=Sem+Foto'; }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="vstudio-badge vstudio-badge-primary" style={{ marginBottom: '0.25rem' }}>
                  {ref.role.toUpperCase()}
                </span>
                <input
                  type="text"
                  className="vstudio-input"
                  style={{ padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                  value={ref.label}
                  onChange={(e) => handleUpdate(idx, { label: e.target.value })}
                  placeholder="Nome do elemento"
                />
              </div>
            </div>

            <div>
              <label className="vstudio-form-label">Regra de Preservação</label>
              <input
                type="text"
                className="vstudio-input"
                style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                value={ref.preservation_notes || ''}
                onChange={(e) => handleUpdate(idx, { preservation_notes: e.target.value })}
                placeholder="Ex: Manter tom de pele e vestimenta profissional"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
              <button
                type="button"
                className="vstudio-btn vstudio-btn-outline vstudio-btn-danger"
                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                onClick={() => handleRemove(idx)}
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="vstudio-card" style={{ borderStyle: 'dashed' }}>
        <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Adicionar Nova Referência</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label className="vstudio-form-label">Tipo de Elemento</label>
            <select
              className="vstudio-select"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as VideoReferenceRole)}
            >
              <option value="avatar">Avatar / Personagem</option>
              <option value="product">Produto / Embalagem</option>
              <option value="brand_asset">Logotipo / Arte da Marca</option>
              <option value="environment">Ambiente / Cenário</option>
              <option value="style">Estilo Visual / Mood</option>
            </select>
          </div>
          <div>
            <label className="vstudio-form-label">Identificação / Rótulo</label>
            <input
              type="text"
              className="vstudio-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ex: Pote de Sorvete 1.5L"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="vstudio-form-label">URL da Imagem de Referência</label>
            <input
              type="url"
              className="vstudio-input"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://exemplo.com/imagem-referencia.jpg"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="vstudio-form-label">Instrução de Preservação</label>
            <input
              type="text"
              className="vstudio-input"
              value={newPreservation}
              onChange={(e) => setNewPreservation(e.target.value)}
              placeholder="Ex: Manter rótulo legível e cores originais da marca"
            />
          </div>
        </div>
        <button
          type="button"
          className="vstudio-btn vstudio-btn-primary"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={handleAdd}
          disabled={!newUrl.trim()}
        >
          + Adicionar à Ficha de Produção
        </button>
      </div>
    </div>
  );
};
